/**
 * GrowthMeasurementModal
 *
 * Modal for adding or editing baby growth measurements.
 * Uses createPortal to render above navigation.
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Scale, Ruler, Circle } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { BabyGrowthEntry } from '@/services/babyGrowthService';
import { CreateBabyGrowthEntry, UpdateBabyGrowthEntry } from '@/services/babyGrowthService';

type WeightUnit = 'g' | 'kg';

interface GrowthMeasurementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    entry: Omit<CreateBabyGrowthEntry, 'pregnancy_id' | 'baby_index'> | UpdateBabyGrowthEntry,
    id?: string
  ) => Promise<void>;
  existingEntry?: BabyGrowthEntry;
  isLoading?: boolean;
}

export function GrowthMeasurementModal({
  isOpen,
  onClose,
  onSave,
  existingEntry,
  isLoading = false,
}: GrowthMeasurementModalProps) {
  const { t } = useTranslation('babyGrowth');
  const [measuredAt, setMeasuredAt] = useState(new Date().toISOString().split('T')[0]);
  const [weightValue, setWeightValue] = useState('');
  const [weightUnit, setWeightUnit] = useState<WeightUnit>('g');
  const [heightCm, setHeightCm] = useState('');
  const [headCm, setHeadCm] = useState('');
  const [notes, setNotes] = useState('');

  // Populate form with existing data
  useEffect(() => {
    if (existingEntry) {
      setMeasuredAt(existingEntry.measured_at);

      if (existingEntry.weight_grams !== null) {
        // Default to grams for values under 1000, kg for larger
        if (existingEntry.weight_grams >= 1000) {
          // Convert to kg with one decimal
          const kgValue = existingEntry.weight_grams / 1000;
          setWeightValue(kgValue.toFixed(2).replace(/\.?0+$/, ''));
          setWeightUnit('kg');
        } else {
          setWeightValue(existingEntry.weight_grams.toString());
          setWeightUnit('g');
        }
      } else {
        setWeightValue('');
        setWeightUnit('g');
      }

      setHeightCm(existingEntry.height_cm !== null ? existingEntry.height_cm.toString() : '');
      setHeadCm(existingEntry.head_circumference_cm !== null ? existingEntry.head_circumference_cm.toString() : '');
      setNotes(existingEntry.notes || '');
    } else {
      // Reset form for new entry
      setMeasuredAt(new Date().toISOString().split('T')[0]);
      setWeightValue('');
      setWeightUnit('g');
      setHeightCm('');
      setHeadCm('');
      setNotes('');
    }
  }, [existingEntry, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Calculate total weight in grams based on unit
    let totalGrams: number | null = null;
    if (weightValue) {
      const numValue = parseFloat(weightValue);
      if (!isNaN(numValue) && numValue > 0) {
        totalGrams = weightUnit === 'kg' ? Math.round(numValue * 1000) : Math.round(numValue);
      }
    }

    // Parse height and head circumference
    const height = heightCm ? parseFloat(heightCm) : null;
    const head = headCm ? parseFloat(headCm) : null;

    // Validate at least one measurement
    if (totalGrams === null && height === null && head === null) {
      return;
    }

    const data = {
      measured_at: measuredAt,
      weight_grams: totalGrams,
      height_cm: height,
      head_circumference_cm: head,
      notes: notes.trim() || null,
    };

    await onSave(data, existingEntry?.id);
  };

  // Common presets for notes
  const NOTE_PRESETS = ['U1', 'U2', 'U3', 'U4', 'U5', 'U6', 'U7', 'U7a', 'U8', 'U9'];

  if (!isOpen) return null;

  const modalContent = (
    <>
      {/* Backdrop */}
      <div className="bottom-sheet-backdrop" onClick={onClose} />

      {/* Modal Container */}
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bottom-sheet-header flex items-center justify-between">
            <h2>{existingEntry ? t('modal.editTitle') : t('modal.newTitle')}</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="bottom-sheet-content space-y-5">
          {/* Date */}
          <div>
            <label className="block text-white/70 text-sm mb-2">{t('modal.date')}</label>
            <input
              type="date"
              value={measuredAt}
              onChange={(e) => setMeasuredAt(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full bg-white/10 border border-white/20 rounded-[10px] px-4 py-3 text-white focus:outline-none focus:border-pp-orange transition-colors"
              required
            />
          </div>

          {/* Weight - Single field with unit dropdown */}
          <div>
            <label className="block text-white/70 text-sm mb-2 flex items-center gap-2">
              <Scale className="w-4 h-4" />
              {t('modal.weight')}
            </label>
            <div className="flex gap-2">
              <div className="flex-1">
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max={weightUnit === 'kg' ? '30' : '30000'}
                  step={weightUnit === 'kg' ? '0.01' : '1'}
                  value={weightValue}
                  onChange={(e) => setWeightValue(e.target.value)}
                  placeholder={weightUnit === 'kg' ? t('modal.weightPlaceholderKg') : t('modal.weightPlaceholderG')}
                  className="w-full bg-white/10 border border-white/20 rounded-[10px] px-4 py-3 text-white focus:outline-none focus:border-pp-orange transition-colors"
                />
              </div>
              <select
                value={weightUnit}
                onChange={(e) => {
                  const newUnit = e.target.value as WeightUnit;
                  // Convert value when switching units
                  if (weightValue) {
                    const numValue = parseFloat(weightValue);
                    if (!isNaN(numValue)) {
                      if (newUnit === 'kg' && weightUnit === 'g') {
                        // g -> kg
                        setWeightValue((numValue / 1000).toFixed(2).replace(/\.?0+$/, ''));
                      } else if (newUnit === 'g' && weightUnit === 'kg') {
                        // kg -> g
                        setWeightValue(Math.round(numValue * 1000).toString());
                      }
                    }
                  }
                  setWeightUnit(newUnit);
                }}
                className="bg-white/10 border border-white/20 rounded-[10px] px-3 py-3 text-white focus:outline-none focus:border-pp-orange transition-colors appearance-none cursor-pointer"
              >
                <option value="g" className="bg-gray-800">{t('modal.unitGrams')}</option>
                <option value="kg" className="bg-gray-800">{t('modal.unitKg')}</option>
              </select>
            </div>
          </div>

          {/* Height */}
          <div>
            <label className="block text-white/70 text-sm mb-2 flex items-center gap-2">
              <Ruler className="w-4 h-4" />
              {t('modal.height')}
            </label>
            <div className="relative">
              <input
                type="number"
                inputMode="decimal"
                min="30"
                max="130"
                step="0.1"
                value={heightCm}
                onChange={(e) => setHeightCm(e.target.value)}
                placeholder={t('modal.heightPlaceholder')}
                className="w-full bg-white/10 border border-white/20 rounded-[10px] px-4 py-3 pr-10 text-white focus:outline-none focus:border-pp-orange transition-colors"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 text-sm">cm</span>
            </div>
          </div>

          {/* Head Circumference */}
          <div>
            <label className="block text-white/70 text-sm mb-2 flex items-center gap-2">
              <Circle className="w-4 h-4" />
              {t('modal.headCircumference')}
            </label>
            <div className="relative">
              <input
                type="number"
                inputMode="decimal"
                min="25"
                max="60"
                step="0.1"
                value={headCm}
                onChange={(e) => setHeadCm(e.target.value)}
                placeholder={t('modal.headPlaceholder')}
                className="w-full bg-white/10 border border-white/20 rounded-[10px] px-4 py-3 pr-10 text-white focus:outline-none focus:border-pp-orange transition-colors"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 text-sm">cm</span>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-white/70 text-sm mb-2">{t('modal.notes')}</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('modal.notesPlaceholder')}
              maxLength={100}
              className="w-full bg-white/10 border border-white/20 rounded-[10px] px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-pp-orange transition-colors"
            />
            {/* Quick presets */}
            <div className="flex flex-wrap gap-2 mt-2">
              {NOTE_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setNotes(preset)}
                  className={`px-3 py-1 rounded-full text-xs transition-colors ${
                    notes === preset
                      ? 'bg-pp-orange text-white'
                      : 'bg-white/10 text-white/60 hover:bg-white/20'
                  }`}
                >
                  {preset}
                </button>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || (!weightValue && !heightCm && !headCm)}
            className="w-full bg-pp-orange text-white font-semibold py-3.5 rounded-[10px] hover:bg-pp-orange/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? t('modal.saving') : existingEntry ? t('modal.update') : t('modal.save')}
          </button>
        </form>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
